import axiosInstance from 'src/utils/axios'

export type AuditManager = {
  _id: string
  name: string
  email: string
}

export type AuditOverviewRow = {
  eventId: string
  name: string
  date?: string
  local: string
  active: boolean
  managers: AuditManager[]
  contractedTerminals: number
  billingNotes: string
  totalOrders: number
  totalRevenue: number
  totalTax: number
  firstSale: string | null
  lastSale: string | null
  terminalsUsed: number
  sellersCount: number
  overLimit: boolean
  undeclared: boolean
  exceededBy?: number
  alertReviewedAt?: string | null
  alertReviewedTerminalsUsed?: number
}

export type AuditOverviewTotals = {
  events: number
  active: number
  withBilling: number
}

export type AuditOrderBy = 'date' | 'activity'

export type AuditOverview = {
  totals: AuditOverviewTotals
  orderBy: AuditOrderBy
  page: number
  perPage: number
  total: number
  totalPages: number
  events: AuditOverviewRow[]
}

export type AuditTerminal = {
  serial: string
  hasSerial: boolean
  orderCount: number
  revenue: number
  firstUse: string | null
  lastUse: string | null
  sellersCount: number
}

export type AuditSeller = {
  sellerId: string
  sellerName: string
  sellerEmail: string
  orderCount: number
  revenue: number
  firstSale: string | null
  lastSale: string | null
  terminalsUsed: number
}

export type AuditEventDetail = {
  event: {
    _id: string
    name: string
    date?: string
    local: string
    address: string
    document: string
    active: boolean
    managers: AuditManager[]
    contractedTerminals: number
    billingNotes: string
  }
  summary: {
    totalOrders: number
    totalRevenue: number
    totalTax: number
    firstSale: string | null
    lastSale: string | null
    terminalsUsed: number
    sellersCount: number
    overLimit: boolean
    undeclared: boolean
  }
  terminals: AuditTerminal[]
  sellers: AuditSeller[]
}

export async function fetchAuditOverview(
  search?: string,
  page = 1,
  perPage = 10,
  orderBy: AuditOrderBy = 'date',
): Promise<AuditOverview> {
  const res = await axiosInstance.get('/audit/overview', {
    params: { search: search || undefined, page, perPage, orderBy },
  })
  return res.data
}

export type AuditAlertsResponse = {
  alerts: AuditOverviewRow[]
  reviewed: AuditOverviewRow[]
}

export async function fetchAuditAlerts(): Promise<AuditAlertsResponse> {
  const res = await axiosInstance.get('/audit/alerts')
  return {
    alerts: res.data.alerts || [],
    reviewed: res.data.reviewed || [],
  }
}

export async function fetchAuditEventDetail(
  eventId: string,
  startDate?: Date | null,
  endDate?: Date | null,
): Promise<AuditEventDetail> {
  const res = await axiosInstance.get(`/audit/event/${eventId}`, {
    params: {
      startDate: startDate ? startDate.toISOString() : undefined,
      endDate: endDate ? endDate.toISOString() : undefined,
    },
  })
  return res.data
}

export async function updateContractedTerminals(
  eventId: string,
  contractedTerminals: number,
  billingNotes?: string,
): Promise<{ eventId: string; contractedTerminals: number; billingNotes: string }> {
  const res = await axiosInstance.patch(`/audit/event/${eventId}/contracted`, {
    contractedTerminals,
    billingNotes,
  })
  return res.data
}

export async function markAuditAlertReviewed(
  eventId: string,
  reviewed: boolean,
): Promise<{
  eventId: string
  reviewed: boolean
  alertReviewedAt: string | null
  alertReviewedTerminalsUsed: number
}> {
  const res = await axiosInstance.patch(`/audit/event/${eventId}/reviewed`, { reviewed })
  return res.data
}
